import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemChips } from "../../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { ACCENT_BLUE, FONT_SIZE, INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { ShiftTypeIcon } from "../../../MasterSchedule/v2/auxillary/ShiftTypeIcon";
import { shiftLabelToShiftType } from "../utils/shiftLabel";

/**
 * The trigger both settings popovers wear.
 *
 * Colocated with the chips rather than duplicated per popover: the Hire tab and the Move tab each own a
 * Settings button, and two 28px boxes that drift apart by a pixel is exactly the kind of difference
 * nobody notices in review and everybody notices on screen. There is no 28px `CONTROL_HEIGHT` token —
 * `MICRO` (24) is documented for actions sitting *inside* content, and these are chrome.
 */
export const SETTINGS_TRIGGER_SX = { minHeight: 28, px: 1, fontSize: FONT_SIZE.LABEL_XS } as const;

const SECTION_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase" as const,
	color: INK.MUTED,
};

/**
 * Selected / deselected shift chip.
 *
 * The previous treatment — selected = white fill, deselected = grey fill — inverted the reading on a
 * white popover: the ON chip looked empty and the OFF chip looked filled, so the group said the
 * opposite of what it meant. Selection is now the FILLED, accent-inked, accent-bordered state and
 * deselection recedes into the surface, which is the direction every other selected thing in this
 * module reads (`ACCENT_BLUE`, the module's fixed interactive accent — a selected hire row, an ON day
 * chip). The nested `&.MuiChip-root` selectors are deliberate: `SystemChips` styles the chip through
 * that same selector, and a bare `backgroundColor` here would tie on specificity and lose.
 */
const SHIFT_CHIP_SELECTED_SX = {
	"&.MuiChip-root": {
		backgroundColor: ACCENT_BLUE.TINT,
		borderColor: ACCENT_BLUE.BORDER,
		color: ACCENT_BLUE.INK,
		fontWeight: 700,
	},
	"&.MuiChip-root:hover": { backgroundColor: ACCENT_BLUE.TINT, color: ACCENT_BLUE.INK },
} as const;

const SHIFT_CHIP_UNSELECTED_SX = {
	"&.MuiChip-root": {
		backgroundColor: SURFACE,
		borderColor: NEUTRAL[300],
		color: INK.MUTED,
		fontWeight: 500,
	},
	"&.MuiChip-root:hover": { backgroundColor: NEUTRAL[50], color: INK.SECONDARY },
} as const;

type Props = {
	/** Every shift type the department runs — the department's own template labels. */
	shiftLabels: string[];
	/** The ones currently in scope. */
	enabledShiftLabels: string[];
	/** One chip toggled. The caller owns the store and refuses a toggle that would leave none on. */
	onToggleShift: (label: string) => void;
};

/**
 * THE page's shift filter, as one labelled chip group.
 *
 * Shared by the Hire and Move settings popovers because it is genuinely ONE control over one store: it
 * scopes the suggested people, the Move cards and the rows of the Recurring Gaps grid together. Each
 * tab surfaces it so you can change scope without leaving the board you are reading, and both write the
 * same preference — so a chip switched off on Move is off on Hire.
 */
export const ShiftScopeChips: FC<Props> = ({ shiftLabels, enabledShiftLabels, onToggleShift }) => {
	const { t } = useTranslation();

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			<Typography sx={SECTION_LABEL_SX}>{t("hiring.hire.settings.shifts")}</Typography>
			<SystemChips
				items={shiftLabels.map((label) => ({
					value: label,
					label: label,
					icon: <ShiftTypeIcon shiftType={shiftLabelToShiftType(label)} />,
				}))}
				selectedItems={enabledShiftLabels.map((label) => ({ value: label, label: label }))}
				variant="outlined"
				selectedSx={SHIFT_CHIP_SELECTED_SX}
				unselectedSx={SHIFT_CHIP_UNSELECTED_SX}
				forceFitContent
				rootSx={{ justifyContent: "flex-start" }}
				dataTestId="hiring-hire-settings-shifts"
				getItemDataTestId={(item) => `hiring-hire-settings-shift-${item.value}`}
				onChange={(item) => onToggleShift(item.value)}
			/>
		</Box>
	);
};

export { SECTION_LABEL_SX };
